import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { APIStatus } from '@application/enums/api-status';
import { getAllPeopleAsync, exportPeopleToExcelAsync } from '@application/features/people/peopleThunk';
import { ActivityTypeKeys, isAnniversaryActivity, isBirthdayActivity } from '@application/features/peopleActivities/constants/activity-types';
import { getCurrentMonthActivitiesAsync, getPeopleMonthlyActivitiesForDashboardsAndCalendarAsync } from '@application/features/peopleActivities/peopleActivitiesThunk';
import { getAllTasksAsync, updateTaskAsync } from '@application/features/task/taskThunk';
import { getAllUsersWithoutPagination } from '@application/features/user/userThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAgeFromDateOfBirth, formatDateOfBirthDisplay } from '@application/utilities/date-helper';
import { isZeroDate } from '@application/utilities/date-utils';
import i18n from '@application/utilities/i18n';
import { Icon } from '@iconify/react';
import { alpha, Box, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
import { DocumentDownload } from 'iconsax-react';
import { StyledMenu } from '@ui/components/common/Menu';
import Grid from '@mui/material/Grid2';
import { ActivityFrame } from '@ui/components/Cards';
import BDrawer from '@ui/components/common/BDrawer';
import CustomDialog from '@ui/components/common/Dialog';
import { PrimaryGrid } from '@ui/components/common/Grid';
import NoDataMessage from '@ui/components/common/NoDataMessage';
import DonutChart from '@ui/components/DonutChart/DonutChart';
import { PageLayout } from '@ui/components/PageLayout';
import { Cake, CalendarTick, Hospital, Profile2User, UserAdd, UserRemove } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FullHeightCard } from '../Home/components';
import CreateTaskDialog from '../Home/components/CreateTaskDialog';
import StyledCalendar from './components/calendar';
import SelectedDateSummary from './components/DateSummaryDialog';
import PeopleCard from './components/People-Card';
import usePeopleStyles from './usePeopleStyles';
const PeopleScreen = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const [selectedDate, setSelectedDate] = useState(null);
    const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const styles = usePeopleStyles();
    const [isCreateTaskDialogOpen, setIsCreateTaskDialogOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const { people, status } = useSelector((state) => state.people);
    const { selectedCompanyId } = useSelector((state) => state.company);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50
    });
    const { tasks, task } = useAppSelector((state) => state.task);
    const userState = useAppSelector((state) => state.user);
    const [selectedActivityId, setSelectedActivityId] = useState(null);
    const [selectedPersonId, setSelectedPersonId] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const currentYear = new Date().getFullYear();
    const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date().getMonth() + 1);
    const [currentCalendarYear, setCurrentCalendarYear] = useState(currentYear);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    useEffect(() => {
        if (!selectedCompanyId)
            return;
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchValue || undefined
        };
        dispatch(getAllPeopleAsync(request));
    }, [dispatch, paginationModel.page, paginationModel.pageSize, selectedCompanyId, searchValue]);
    useEffect(() => {
        // Only make API call if we have a selected company. Refetch when language changes for translated activity data.
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getCurrentMonthActivitiesAsync(selectedCompanyId));
            dispatch(getAllTasksAsync());
            dispatch(getAllUsersWithoutPagination());
        }
    }, [selectedCompanyId, dispatch, i18n.language]);
    useEffect(() => {
        // Fetch data when calendar month/year or language changes (language change for translated activity data)
        // Only fetch data for the current year
        const currentYear = new Date().getFullYear();
        if (selectedCompanyId && selectedCompanyId.length > 0 && currentCalendarYear === currentYear) {
            dispatch(getPeopleMonthlyActivitiesForDashboardsAndCalendarAsync({
                companyId: selectedCompanyId,
                month: currentCalendarMonth,
                year: currentCalendarYear
            }));
        }
    }, [currentCalendarMonth, currentCalendarYear, dispatch, selectedCompanyId, i18n.language]);
    useEffect(() => {
        if (task && selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getCurrentMonthActivitiesAsync(selectedCompanyId));
        }
    }, [task, dispatch]);
    const handleCalendarDateClick = (date) => {
        setSelectedDate(date);
        setIsDateDialogOpen(true);
    };
    const handleCloseDateDialog = () => {
        setIsDateDialogOpen(false);
        setSelectedDate(null);
    };
    // Assigned user logic
    const userOptions = userState.usersWithoutPagination.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id
    }));
    // Update Task Assigned User logic
    const handleUserSelection = async (ActivityId, newSelectedUsers) => {
        setSelectedActivityId(ActivityId);
        setSelectedUsers(newSelectedUsers || []);
    };
    useEffect(() => {
        if (selectedActivityId && selectedUsers) {
            const updateTaskIfReady = async () => {
                let Updatetask = task?.activityId === selectedActivityId ? task : undefined;
                if (!Updatetask) {
                    Updatetask = tasks?.find((t) => t.activityId === selectedActivityId);
                }
                if (!Updatetask)
                    return;
                const updateRequest = {
                    id: Updatetask.id,
                    name: Updatetask.name,
                    deadline: Updatetask.deadline,
                    priority: Updatetask.priority,
                    completionStatus: Updatetask.completionStatus,
                    assignedTo: selectedUsers.length > 0 ? selectedUsers : null,
                    eventId: Updatetask?.eventId || null,
                    isActivityTask: Updatetask?.isActivityTask,
                    activityId: Updatetask?.activityId,
                    relationId: Updatetask?.relationId,
                    personId: Updatetask?.personId,
                    taskType: Updatetask?.taskType
                };
                await dispatch(updateTaskAsync(updateRequest)).unwrap();
            };
            updateTaskIfReady();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedActivityId, selectedUsers]);
    const handleOpenCreateTaskDialog = (activityId, personId) => {
        setSelectedActivityId(activityId ? activityId : null);
        setSelectedPersonId(personId ? personId : null);
        setIsCreateTaskDialogOpen(true);
    };
    const handleCloseCreateTaskDialog = () => {
        setIsCreateTaskDialogOpen(false);
    };
    const handleSearchChange = (value) => {
        setSearchValue(value);
        // Reset to first page when searching
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };
    const handleCalendarMonthChange = (month, year) => {
        // Restrict to current year only
        const currentYear = new Date().getFullYear();
        if (year !== currentYear) {
            // If trying to navigate outside current year, reset to current year
            setCurrentCalendarMonth(new Date().getMonth() + 1);
            setCurrentCalendarYear(currentYear);
            return;
        }
        setCurrentCalendarMonth(month);
        setCurrentCalendarYear(year);
    };
    const handleFilterClick = () => { };
    const isBelow1280 = useMediaQuery(theme.breakpoints.down(1280));
    const is1280To1900 = useMediaQuery(theme.breakpoints.between(1280, 1900));
    const is1900Up = useMediaQuery(theme.breakpoints.up(1900));
    const is600To800 = useMediaQuery(theme.breakpoints.between(600, 800));
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { activities, monthlyActivitiesForDashboard } = useAppSelector((state) => state.PeopleActivity);
    const { birthdaysCount, anniversariesCount, birthdayItems, anniversaryItems } = useMemo(() => {
        if (!selectedDate || !monthlyActivitiesForDashboard?.calendarActivities) {
            return { birthdaysCount: 0, anniversariesCount: 0, birthdayItems: [], anniversaryItems: [] };
        }
        const selectedDay = selectedDate.getDate();
        // Find the calendar activity for the selected date
        const calendarActivity = monthlyActivitiesForDashboard.calendarActivities.find((activity) => activity.day === selectedDay);
        if (!calendarActivity) {
            return { birthdaysCount: 0, anniversariesCount: 0, birthdayItems: [], anniversaryItems: [] };
        }
        // Filter activities by type using stable activityTypeCode (language-independent)
        const birthdayActivities = calendarActivity.activities.filter((activity) => isBirthdayActivity(activity.activityTypeCode || activity.activityType));
        const anniversaryActivities = calendarActivity.activities.filter((activity) => isAnniversaryActivity(activity.activityTypeCode || activity.activityType));
        const birthdayItems = birthdayActivities.map((activity) => {
            const age = getAgeFromDateOfBirth(activity.dateOfBirth);
            return {
                id: activity.personId,
                text: activity.description,
                personName: activity.personName,
                metaLeft: age != null ? `${age} ${t('common.yearsOld')}` : undefined,
                metaRight: formatDateOfBirthDisplay(activity.dateOfBirth) || undefined
            };
        });
        const anniversaryItems = anniversaryActivities.map((activity) => {
            const isWorkAnniversary = (activity.activityTypeCode || activity.activityType) === ActivityTypeKeys.WorkAnniversary;
            return {
                id: activity.personId,
                text: activity.description,
                personName: activity.personName,
                metaLeft: isWorkAnniversary ? activity.companyName || undefined : undefined,
                metaRight: isWorkAnniversary
                    ? formatDateOfBirthDisplay(activity.employmentStartDate) || undefined
                    : undefined
            };
        });
        return {
            birthdaysCount: birthdayActivities.length,
            anniversariesCount: anniversaryActivities.length,
            birthdayItems,
            anniversaryItems
        };
    }, [selectedDate, monthlyActivitiesForDashboard, t]);
    // Helper to get task status for an activity
    const GetTaskStatus = (activityId) => {
        const taskForActivity = tasks?.find((t) => t.activityId == activityId);
        if (taskForActivity) {
            return taskForActivity.completionStatus;
        }
        if (task?.activityId == activityId) {
            return task?.completionStatus;
        }
        return undefined;
    };
    const peopleRows = people?.items?.map((person) => ({
        id: person.id,
        contact: person.name,
        designation: person.position,
        employmentStartDate: person.employmentStartDate,
        dateOfBirth: person.dateOfBirth,
        family: `${person.familyMembers}`,
        activities: `${person.activities}`,
        email: person.email
    })) || [];
    const peopleColumns = [
        {
            field: 'contact',
            headerName: t('people.contact'),
            minWidth: 180,
            flex: 2,
            renderCell: (params) => {
                const name = params.value;
                const email = params.row.email;
                return (_jsx(Box, { display: 'flex', alignItems: 'center', gap: 1.5, my: 1, flex: 1, minWidth: 0, sx: {
                        cursor: 'pointer'
                    }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, children: [name && (_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, children: name })), _jsx(Typography, { variant: 'body2', color: email ? 'text.secondary' : theme.palette.error.main, noWrap: true, children: email ? email : t('people.missingEmail') })] }) }));
            }
        },
        {
            field: 'company',
            headerName: t('people.company'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const company = people?.items.find((p) => p.id === params.id)?.peopleCompany || '';
                return (_jsx(Box, { display: 'flex', alignItems: 'center', gap: 1.5, my: 1, flex: 1, minWidth: 0, sx: {
                        cursor: 'pointer'
                    }, children: _jsx(Box, { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', noWrap: true, textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '100%', minWidth: 0, flex: 1, children: company }) }) }));
            }
        },
        { field: 'designation', headerName: t('people.designation'), flex: 1, minWidth: 150 },
        {
            field: 'employmentStartDate',
            headerName: t('people.joiningDate'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const dateValue = params.value;
                if (!dateValue || isZeroDate(String(dateValue)))
                    return '';
                return new Date(dateValue).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric'
                });
            }
        },
        {
            field: 'dateOfBirth',
            headerName: t('people.dateOfBirth'),
            flex: 0.8,
            minWidth: 150,
            renderCell: (params) => {
                const dateValue = params.value;
                if (!dateValue || isZeroDate(String(dateValue)))
                    return '';
                return new Date(dateValue).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric'
                });
            }
        },
        { field: 'family', headerName: t('people.family'), flex: 0.8, minWidth: 150 },
        {
            field: 'activities',
            headerName: t('people.activities'),
            headerAlign: 'right',
            align: 'right',
            flex: 1,
            minWidth: 150
        }
    ];
    const overviewData = monthlyActivitiesForDashboard?.overview
        ? (() => {
            // 17 distinct colors - keeping original 6 colors in their original order
            const allColors = [
                theme.palette.primary.main,
                theme.palette.success.main,
                theme.palette.info.light,
                theme.palette.warning.light,
                theme.palette.error.main,
                theme.palette.secondary.main,
                theme.palette.info.main,
                theme.palette.warning.main,
                theme.palette.primary.light,
                theme.palette.success.light,
                theme.palette.error.light,
                theme.palette.primary.dark,
                theme.palette.success.dark,
                theme.palette.info.dark,
                theme.palette.warning.dark,
                theme.palette.error.dark,
                theme.palette.gold.light
            ];
            const filteredData = monthlyActivitiesForDashboard.overview.designationCounts.filter((dc) => dc.count > 0);
            return {
                labels: filteredData.map((dc) => dc.designation),
                values: filteredData.map((dc) => dc.count),
                colors: filteredData.map((_, index) => allColors[index % allColors.length])
            };
        })()
        : {
            labels: ['Steermeesters', 'Directors', 'Happiness Off', 'VDK Group (Landelijk)'],
            values: [60, 15, 15, 10],
            colors: [
                theme.palette.primary.main,
                theme.palette.success.main,
                theme.palette.info.light,
                theme.palette.warning.light
            ]
        };
    const upcomingData = monthlyActivitiesForDashboard?.upcomingEvents
        ? (() => {
            const code = (e) => e.activityTypeCode || e.eventType;
            const birthdays = monthlyActivitiesForDashboard.upcomingEvents.filter((event) => isBirthdayActivity(code(event))).length;
            const anniversaries = monthlyActivitiesForDashboard.upcomingEvents.filter((event) => isAnniversaryActivity(code(event))).length;
            const labels = [];
            const values = [];
            const colors = [];
            if (anniversaries > 0) {
                labels.push(t('people.anniversaries'));
                values.push(anniversaries);
                colors.push(theme.palette.primary.main);
            }
            if (birthdays > 0) {
                labels.push(t('people.birthdays'));
                values.push(birthdays);
                colors.push(theme.palette.success.main);
            }
            return { labels, values, colors };
        })()
        : {
            labels: [t('people.anniversaries'), t('people.birthdays')],
            values: [30, 45],
            colors: [theme.palette.primary.main, theme.palette.success.main]
        };
    const hasOverviewData = overviewData.values.length > 0 && overviewData.values.some((v) => v > 0);
    const hasUpcomingData = upcomingData.values.length > 0 && upcomingData.values.some((v) => v > 0);
    const eventList = monthlyActivitiesForDashboard?.calendarActivities
        ? monthlyActivitiesForDashboard.calendarActivities.flatMap((activity) => {
            const events = [];
            if (activity.hasBirthday) {
                events.push({
                    date: activity.day,
                    type: t('people.birthdays')
                });
            }
            if (activity.hasAnniversary) {
                events.push({
                    date: activity.day,
                    type: t('people.anniversaries')
                });
            }
            return events;
        })
        : [
            { date: 3, type: t('people.anniversaries') },
            { date: 3, type: t('people.birthdays') },
            { date: 7, type: t('people.anniversaries') },
            { date: 21, type: t('people.birthdays') }
        ];
    const eventColorMap = upcomingData.labels.reduce((acc, label, index) => {
        acc[label] = upcomingData.colors[index];
        return acc;
    }, {});
    // Fallback: if upcomingEvents is empty but calendarActivities has data, use default colors
    if (Object.keys(eventColorMap).length === 0 &&
        monthlyActivitiesForDashboard?.calendarActivities) {
        const hasBirthday = monthlyActivitiesForDashboard.calendarActivities.some((a) => a.hasBirthday);
        const hasAnniversary = monthlyActivitiesForDashboard.calendarActivities.some((a) => a.hasAnniversary);
        if (hasAnniversary)
            eventColorMap[t('people.anniversaries')] = theme.palette.primary.main;
        if (hasBirthday)
            eventColorMap[t('people.birthdays')] = theme.palette.success.main;
    }
    return (_jsxs(PageLayout, { title: t('common.people'), secondTitle: t('people.accessAndManageAllPeopleRecordsHere'), showLeftIcon: true, leftIcon: _jsx(Profile2User, { variant: 'Outline' }), children: [_jsxs(Box, { id: 'page__people', sx: { ...styles.main }, children: [isSmallScreen && (_jsx(_Fragment, { children: _jsx(Box, { sx: { ...styles.mobileScreenHeader } }) })), _jsx(Box, { id: 'page__content-container', sx: { ...styles.contentContainer }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { container: true, size: { xs: 12, md: 12, lg: isBelow1280 ? 12 : 6, xl: 8 }, spacing: 2, sx: {
                                        height: isBelow1280 ? undefined : 'calc(100dvh - 130px)',
                                        overflowY: isBelow1280 ? 'hidden' : 'auto',
                                        pr: 0.5
                                    }, children: [_jsx(Grid, { size: { xs: 12, md: 12, lg: isBelow1280 ? 12 : 4, xl: is1280To1900 ? 12 : 5 }, sx: { height: { xs: 'auto', xxl: is1900Up ? '35%' : 'auto' } }, children: _jsx(PeopleCard, { heading: t('people.overview'), variant: 'grey', disableShadow: true, headerTypographyVariant: 'h6', children: _jsx(Box, { height: '100%', width: '100%', children: hasOverviewData ? (_jsx(DonutChart, { title: '', fillParent: true, centerText: monthlyActivitiesForDashboard?.overview?.totalCount?.toString(), data: overviewData })) : (_jsx(DonutChart, { data: { labels: [], values: [], colors: [] }, centerText: t('events.noData'), centerSubtext: t('events.noData'), counterContentSx: { top: '50%' }, counterContentStyle: { fontSize: '15px', fontWeight: 400 } })) }) }) }), _jsx(Grid, { size: { xs: 12, md: 12, lg: isBelow1280 ? 12 : 4, xl: is1280To1900 ? 12 : 7 }, sx: { height: { xs: 'auto', xxl: is1900Up ? '35%' : 'auto' } }, children: _jsx(PeopleCard, { heading: t('people.upcomingNextMonth'), haveHeader: false, sx: { height: '100%', minHeight: 0 }, variant: 'grey', disableShadow: true, headerTypographyVariant: 'h6', children: _jsxs(Box, { display: 'flex', flexDirection: isXs || is600To800 ? 'column' : 'row', gap: { xs: 1 }, width: '100%', padding: '12px', height: '100%', sx: { flexGrow: 1, minHeight: 0 }, children: [_jsx(Box, { flex: 1, minHeight: 0, mt: 1, children: hasUpcomingData ? (_jsx(DonutChart, { title: t('people.upcoming'), centerTitle: false, centerText: monthlyActivitiesForDashboard?.upcomingEvents?.length?.toString(), data: upcomingData, counterContentSx: { top: '40%' }, fillParent: true })) : (_jsx(DonutChart, { title: t('people.upcoming'), centerTitle: false, data: { labels: [], values: [], colors: [] }, centerText: t('events.noData'), centerSubtext: t('events.noData'), counterContentSx: { top: '50%' }, counterContentStyle: { fontSize: '15px', fontWeight: 400 } })) }), _jsx(StyledCalendar, { selectedDates: monthlyActivitiesForDashboard?.calendarActivities?.map((activity) => activity.day) || [3], highlightedDates: monthlyActivitiesForDashboard?.calendarActivities
                                                                ?.filter((activity) => activity.hasBirthday)
                                                                .map((activity) => activity.day) || [21], events: eventList, eventColors: eventColorMap, onMonthChange: handleCalendarMonthChange, onDateClick: handleCalendarDateClick })] }) }) }), _jsx(Grid, { size: { xs: 12, md: 12, lg: isBelow1280 ? 12 : 4, xl: 12 }, sx: { height: { xs: 'auto', xxl: is1900Up ? '63%' : 'auto' } }, children: _jsx(PeopleCard, { heading: t('common.people'), headerTypographyVariant: 'h4', cardHeaderPadding: '12px 0px 12px 0.5px', cardContentContainersx: { padding: 0 }, leftActionNode: _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'primary', size: 'large', startIcon: _jsx(DocumentDownload, { variant: 'Outline', size: 20 }), onClick: (e) => setExportAnchorEl(e.currentTarget), children: t('events.eventDetails.passportTab.exportAsExcel') }), _jsx(StyledMenu, { anchorEl: exportAnchorEl, open: Boolean(exportAnchorEl), onClose: () => setExportAnchorEl(null), actions: [
                                                                {
                                                                    text: t('common.exportAll', 'Export all'),
                                                                    group: true,
                                                                    to: () => {
                                                                        setExportAnchorEl(null);
                                                                        dispatch(exportPeopleToExcelAsync({
                                                                            searchString: undefined,
                                                                            emailFilter: 'all'
                                                                        })); // add SearchValue if you want to apply search filter to export
                                                                    }
                                                                },
                                                                {
                                                                    text: t('people.withEmailAddress', 'People with email address'),
                                                                    group: true,
                                                                    to: () => {
                                                                        setExportAnchorEl(null);
                                                                        dispatch(exportPeopleToExcelAsync({
                                                                            searchString: undefined,
                                                                            emailFilter: 'withEmail'
                                                                        })); // add SearchValue if you want to apply search filter to export
                                                                    }
                                                                },
                                                                {
                                                                    text: t('people.withoutEmailAddress', 'People without email address'),
                                                                    group: true,
                                                                    to: () => {
                                                                        setExportAnchorEl(null);
                                                                        dispatch(exportPeopleToExcelAsync({
                                                                            searchString: undefined,
                                                                            emailFilter: 'withoutEmail'
                                                                        })); // add SearchValue if you want to apply search filter to export
                                                                    }
                                                                }
                                                            ] })] }), showSearch: true, 
                                                //showFilters={true}
                                                searchValue: searchValue, onSearchChange: handleSearchChange, onFilterClick: handleFilterClick, disableBackground: true, disableShadow: true, disableBorders: true, children: _jsx(PrimaryGrid, { rows: peopleRows, columns: peopleColumns, loading: status === APIStatus.PENDING, rowCount: people?.totalCount || 0, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', noRowsText: t('dataGrid.noRows', {
                                                        entityPlural: t('common.people').toLowerCase?.(),
                                                        entitySingular: t('common.people').toLowerCase?.()
                                                    }), showHeaderControls: false, onRowClick: (params) => navigate(`/people/${params.id}`), hideFooter: true, getRowClassName: (params) => {
                                                        const e = params.row.email;
                                                        return e && String(e).trim() ? '' : 'row--hasNot-email';
                                                    }, sx: {
                                                        '& .row--hasNot-email': {
                                                            backgroundColor: theme.palette.background.neutral
                                                        },
                                                        '& .row--hasNot-email .MuiDataGrid-cell': {
                                                            backgroundColor: theme.palette.background.neutral
                                                        }
                                                    }, boxProps: {
                                                        sx: {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }, style: { height: '100%' } }) }) })] }), _jsx(Grid, { size: { xs: 12, md: 12, lg: isBelow1280 ? 12 : 6, xl: 4 }, sx: { ...styles.gridItem }, children: _jsx(FullHeightCard, { heading: t('people.peopleActivity'), infoTooltip: t('tooltips.peopleActivity'), disableBackground: true, disableBorders: true, disableShadow: true, headerTypographyVariant: 'h4', headerTypographyColor: theme.palette.text.primary, filter: false, cardHeaderPadding: '0px 0px 12px 4px', cardContentContainersx: { padding: '0px' }, info: _jsx(Icon, { icon: 'hugeicons:information-circle', fontSize: 21.67, color: theme.palette.primary.darker }), children: _jsx(Box, { className: 'activity_frames_container', sx: { ...styles.activityFramesContainer }, children: activities && activities.length > 0 ? (activities.map((activity, index) => (_jsx(ActivityFrame, { variant: 'light', type: activity.isTaskAssigned ? 'task-assigned' : 'create-task', text: activity.activityMessage, personName: activity.personName, personId: activity.personId, metaLeft: isBirthdayActivity(activity.activityTypeCode || activity.activityType) &&
                                                    activity.dateOfBirth
                                                    ? (() => {
                                                        const age = getAgeFromDateOfBirth(activity.dateOfBirth);
                                                        return age != null ? `${age} ${t('common.yearsOld')}` : undefined;
                                                    })()
                                                    : (activity.activityTypeCode || activity.activityType) ===
                                                        ActivityTypeKeys.EmployeeOnboarding
                                                        ? activity.company || undefined
                                                        : undefined, metaRight: isBirthdayActivity(activity.activityTypeCode || activity.activityType) &&
                                                    activity.dateOfBirth
                                                    ? formatDateOfBirthDisplay(activity.dateOfBirth)
                                                    : (activity.activityTypeCode || activity.activityType) ===
                                                        ActivityTypeKeys.EmployeeOnboarding &&
                                                        activity.personActivityDate
                                                        ? formatDateOfBirthDisplay(activity.personActivityDate)
                                                        : undefined, 
                                                // subText={
                                                //   isBirthdayActivity(activity.activityTypeCode || activity.activityType)
                                                //     ? t<string>('common.lastActivity', {
                                                //         date: GetFormattedDate(
                                                //           activity.personActivityDate,
                                                //           activity.activityTypeCode || activity.activityType
                                                //         )
                                                //       })
                                                //     : ''
                                                // }
                                                subTextIcon: (() => {
                                                    const typeCode = activity.activityTypeCode || activity.activityType;
                                                    const iconBox = (bgColor, icon) => (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 1.5, sx: { backgroundColor: bgColor }, children: icon }));
                                                    if (isBirthdayActivity(typeCode))
                                                        return iconBox(alpha(theme.palette.info.main, 0.08), _jsx(Cake, { variant: 'Outline', size: '20', color: theme.palette.success.main, style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.EmployeeIllness)
                                                        return iconBox('#E3F2FD', _jsx(Hospital, { variant: 'Outline', width: 20, height: 20, color: '#42A5F5', style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.EmployeeDeath)
                                                        return iconBox('#C4CDD5', _jsx(Icon, { icon: 'solar:heart-bold', width: 20, height: 20, color: '#212B36', style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.EmployeeDeparture)
                                                        return iconBox('#FF563014', _jsx(UserRemove, { variant: 'Outline', width: 20, height: 20, color: '#42A5F5', style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.EmployeeDeath)
                                                        return iconBox('#C4CDD5', _jsx(Icon, { icon: 'solar:heart-bold', width: 20, height: 20, color: '#212B36', style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.EmployeeDeparture)
                                                        return iconBox('#FF563014', _jsx(UserRemove, { variant: 'Outline', size: '20', color: '#B71D18', style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.EmployeeOnboarding)
                                                        return iconBox('#00B8D914', _jsx(UserAdd, { variant: 'Outline', size: '20', color: '#006C9C', style: { flexShrink: 0 } }));
                                                    if (typeCode === ActivityTypeKeys.BirthAdoption)
                                                        return iconBox('#22C55E14', _jsx(Icon, { icon: 'solar:confetti-bold', width: 20, height: 20, color: '#118D57', style: { flexShrink: 0 } }));
                                                    if (isAnniversaryActivity(typeCode) ||
                                                        typeCode === ActivityTypeKeys.Retirement)
                                                        return iconBox('#E8F5E9', _jsx(Icon, { icon: 'solar:confetti-bold', width: 20, height: 20, color: '#66BB6A', style: { flexShrink: 0 } }));
                                                    return iconBox(alpha(theme.palette.success.main, 0.08), _jsx(CalendarTick, { variant: 'Outline', size: '20', color: theme.palette.primary.main, style: { flexShrink: 0 } }));
                                                })(), assignedUsers: activity.assignedTo, availableUsers: userOptions.map((user) => ({
                                                    id: user.value,
                                                    name: user.label
                                                })), taskStatus: GetTaskStatus(activity.id), onTaskCreate: () => handleOpenCreateTaskDialog(activity.id, activity.personId), onUserAssign: handleUserSelection, activityId: activity.id }, activity.id)))) : (_jsx(NoDataMessage, { text: t('common.noDataAvailable', { data: t('common.people') }) })) }) }) })] }) })] }), _jsx(CreateTaskDialog, { open: isCreateTaskDialogOpen, ActivityId: selectedActivityId, PersonId: selectedPersonId, onClose: handleCloseCreateTaskDialog }), selectedDate && !isSmallScreen && (_jsx(CustomDialog, { open: isDateDialogOpen, onClose: handleCloseDateDialog, title: t('common.events'), maxWidth: 'md', backgroundVariant: 'grey', closeIcon: true, children: _jsx(Box, { sx: { p: 2 }, children: _jsx(SelectedDateSummary, { date: selectedDate, birthdaysCount: birthdaysCount, anniversariesCount: anniversariesCount, birthdayItems: birthdayItems, anniversaryItems: anniversaryItems, listMaxHeight: '300px' }) }) })), isSmallScreen && (_jsx(BDrawer, { open: isDateDialogOpen, onClose: handleCloseDateDialog, title: t('common.events'), anchor: 'bottom', showBottomBar: false, children: _jsx(Box, { sx: { p: 2 }, children: _jsx(SelectedDateSummary, { date: selectedDate, birthdaysCount: birthdaysCount, anniversariesCount: anniversariesCount, birthdayItems: birthdayItems, anniversaryItems: anniversaryItems }) }) }))] }));
};
export default PeopleScreen;
